import React, { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeProps,
  type Connection,
  type EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  BaseEdge,
  useStore,
  useReactFlow,
  NodeToolbar,
  SelectionMode,
  type OnSelectionChangeParams,
  type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from '../icons/AppIcon';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { FlowNode, FlowNodeType, FlowRunHighlight, NODE_TYPE_META, getNodeEdges, suggestNextNodeTypes } from './flow-types';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * Web-only visual canvas for editing a Flow's graph — draggable, typed node cards connected
 * by lines you can draw yourself. Each node type (send message, ask & wait, branch,
 * webhook, ...) renders with its own icon/color from NODE_TYPE_META. Every one of a node's
 * outgoing connections (buttons, branch condition groups, or a single "next") gets its OWN
 * named source handle on the card, positioned along the bottom edge — drag from a handle to
 * another node to wire that specific connection; drag an existing edge off a node to delete
 * it. Each node has a single target handle, but the visual entry point on that node's border
 * is recomputed per-edge from its live position (see getCirclePoint/LabeledEdge), so moving a
 * connected node to the side routes the line into its side instead of always the top.
 * onConnect/onEdgesDelete translate drag gestures back into nextNodeId writes via
 * onConnectEdge/onDeleteEdge, owned by the parent (FlowForm), so the canvas stays
 * presentation-only and validation/state logic isn't duplicated.
 *
 * Only ever bundled for the web build: Metro resolves this .web.tsx file over any non-.web
 * sibling when Platform.OS === 'web' (same pattern as index.web.tsx), because reactflow is a
 * DOM/SVG library that cannot run in the native app.
 */

export interface FlowCanvasProps {
  nodes: FlowNode[];
  entryNodeId: string;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  onAddNode: () => void;
  onMoveNode: (nodeId: string, position: { x: number; y: number }) => void;
  nodePositions: Record<string, { x: number; y: number }>;
  /** Called when the user drags a connection from one node's edge-handle to another node —
   * (sourceNodeId, edgeKey, targetNodeId) so the parent can write nextNodeId into the right
   * place (a button, a branch group, or the node's plain "next"). */
  onConnectEdge: (sourceNodeId: string, edgeKey: string, targetNodeId: string) => void;
  /** Called when the user deletes a drawn edge (drag off, select + Delete key, or the
   * Controls trash) — (sourceNodeId, edgeKey) so the parent can clear that connection. */
  onDeleteEdge: (sourceNodeId: string, edgeKey: string) => void;
  /** Called with the full set of currently multi-selected node ids (shift-click or rubber-band
   * drag) — separate from selectedNodeId/onSelectNode above, which still drives the single-select
   * inspector-panel-open behavior unchanged. This is the foundation for future bulk actions
   * (multi-delete/duplicate/move); nothing reads it yet. */
  onSelectionChange?: (nodeIds: string[]) => void;
  /** Called once when a MULTI-node drag (2+ selected nodes moved together) ends, with every
   * moved node's final position — lets the parent commit the whole group move as a single undo
   * step instead of one step per node (which the existing onMoveNode, called per-node for
   * single-node drags, would otherwise produce if used for group drags too). */
  onMoveNodes?: (moves: { nodeId: string; position: { x: number; y: number } }[]) => void;
  /** Selected node ids the bulk-action toolbar should act on — same array reported via
   * onSelectionChange, passed back in so the canvas can render the toolbar itself. */
  selectedNodeIds?: string[];
  onDuplicateSelected?: () => void;
  onDeleteSelected?: () => void;
  /** Selected node's suggestions: add a step on one of its open connections — with a type it
   * is added right away, without one the parent opens the type picker. */
  onAddNodeFrom?: (sourceNodeId: string, edgeKey: string, type?: FlowNodeType) => void;
  /** Filled by the canvas: the flow-coordinate centre of what is on screen, for placing new nodes. */
  viewCenterRef?: React.MutableRefObject<(() => { x: number; y: number }) | null>;
  /** What the selected node still needs set up (shown in its suggestions panel). */
  selectedNodeIssues?: string[];
  onEditNode?: (nodeId: string) => void;
  onDuplicateNode?: (nodeId: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  /** A click on empty canvas: close the selection (and its suggestions). */
  onClearSelection?: () => void;
  /** A run to draw over the graph: its steps numbered in order, its path highlighted, the rest dimmed. */
  runHighlight?: FlowRunHighlight | null;
  /** Renames a connection (double-click a line): its button / group / category / rule / route name, or a custom label. */
  onRenameEdge?: (sourceNodeId: string, edgeKey: string, text: string) => void;
  /** Deletes steps and lines together (after the canvas's confirmation) as one undo step. */
  onDeleteItems?: (nodeIds: string[], edges: Array<{ sourceNodeId: string; edgeKey: string }>) => void;
}

/** Where open connection dots sit: under the circle (top-to-bottom flows) or on its right (left-to-right). */
type Direction = 'vertical' | 'horizontal';
const DIRECTION_KEY = 'dislix.flowCanvas.direction';
const readDirection = (): Direction => {
  try {
    return window.localStorage.getItem(DIRECTION_KEY) === 'horizontal' ? 'horizontal' : 'vertical';
  } catch {
    return 'vertical';
  }
};

/** Circle size on the canvas; the node box is NODE_WIDTH wide with the name under the circle. */
const CIRCLE = 52;
const NODE_WIDTH = 120;

/** Where source handle i of n sits on the circle's lower arc (as % of the circle box), so a
 * node with several outgoing connections fans them out instead of stacking them. */
function arcPoint(i: number, n: number, direction: Direction): { left: string; top: string } {
  const spread = n === 1 ? 0 : Math.PI * (0.35 - (0.7 * i) / (n - 1));
  // vertical: lower arc, left to right (153° → 27°); horizontal: right arc, top to bottom (-63° → 63°)
  const angle = direction === 'vertical' ? Math.PI / 2 + spread : -spread;
  return { left: `${50 + 50 * Math.cos(angle)}%`, top: `${50 + 50 * Math.sin(angle)}%` };
}

type RunMark = { order: number[]; error: boolean; waiting: boolean };

const FlowNodeCardImpl: React.FC<NodeProps<{ node: FlowNode; isEntry: boolean; isSelected: boolean; onSelect: () => void; run?: RunMark; dimmed?: boolean; direction: Direction }>> = ({ data, selected }) => {
  const cardStyles = useThemedStyles(getCardStyles);
  const { node, isEntry, onSelect, run, dimmed, direction } = data;
  const isSelected = data.isSelected || selected;
  const meta = NODE_TYPE_META.find((m) => m.type === node.type);
  const color = meta?.color || (colors.primary as string);
  const edges = node.type === 'end' ? [] : getNodeEdges(node);
  const handles = node.type === 'end' ? [] : edges.length > 0 ? edges : [{ key: 'default', label: 'Next', nextNodeId: undefined }];
  // Only open connections show a dot to drag from; connected ones keep an invisible handle
  // (React Flow needs it) — their line leaves from the side facing its target instead.
  const open = handles.filter((h) => !h.nextNodeId);
  const isStart = node.type === 'start';
  const title = node.name || meta?.label || 'Untitled checker';
  return (
    <TouchableOpacity onPress={onSelect} activeOpacity={0.85} accessibilityLabel={`${title} (${meta?.label || node.type})`}>
      <View style={[cardStyles.node, dimmed && { opacity: 0.3 }]}>
        <View
          style={[
            cardStyles.circle,
            { borderColor: color, backgroundColor: isSelected ? color : `${color}1F` },
            isStart && cardStyles.startCircle,
            isSelected && { boxShadow: `0 0 0 4px ${color}40` } as any,
          ]}
        >
          {/* Start has no target handle — nothing can connect INTO it. Every other node's single
              target handle is invisible; lines float to the circle's edge (see LabeledEdge). */}
          {!isStart && <Handle type="target" position={Position.Top} style={handleHiddenCss} />}
          <AppIcon name={(meta?.icon || 'ellipse-outline')} size={24} color={isSelected ? (colors.white as string) : color} />
          {run && (
            <View style={[cardStyles.runBadge, { backgroundColor: run.error ? (colors.error as string) : run.waiting ? (colors.warning as string) : (colors.status.success as string) }]}>
              <Text style={cardStyles.runBadgeText}>{run.order.slice(0, 3).join(',')}{run.order.length > 3 ? '…' : ''}</Text>
            </View>
          )}
          {isEntry && !isStart && (
            <View style={[cardStyles.entryBadge, { backgroundColor: color }]}>
              <Ionicons name="flag" size={9} color={colors.white as string} />
            </View>
          )}
          {/* One named source handle per outgoing connection, fanned along the lower arc — drag
              from one to wire that specific connection (button, rule, route or "next"). */}
          {handles.map((edge) => {
            const openIndex = open.indexOf(edge);
            return (
              <Handle
                key={edge.key}
                id={edge.key}
                type="source"
                position={direction === 'vertical' ? Position.Bottom : Position.Right}
                title={`${edge.label} — drag to a step to connect`}
                style={openIndex >= 0
                  ? { ...handleCss, ...arcPoint(openIndex, open.length, direction), bottom: 'auto', right: 'auto', transform: 'translate(-50%, -50%)' }
                  : { ...handleHiddenCss, left: '50%', top: '50%', bottom: 'auto', right: 'auto' }}
              />
            );
          })}
        </View>
        <Text style={[cardStyles.title, isSelected && { color }]} numberOfLines={2}>{title}</Text>
      </View>
    </TouchableOpacity>
  );
};

// onSelect is a fresh closure every parent render (see rfNodes below), so comparing it would
// defeat memoization entirely — only re-render when the fields that actually affect this
// card's output change. node is a fresh object per parent render too whenever ANY node in the
// flow changes (rfNodes recomputes the whole array), so compare its meaningful fields instead
// of referential identity.
const FlowNodeCard = React.memo(FlowNodeCardImpl, (prev, next) => {
  const a = prev.data;
  const b = next.data;
  return (
    a.isEntry === b.isEntry &&
    a.isSelected === b.isSelected &&
    prev.selected === next.selected &&
    a.direction === b.direction &&
    a.run === b.run &&
    a.dimmed === b.dimmed &&
    a.node.id === b.node.id &&
    a.node.type === b.node.type &&
    a.node.name === b.node.name &&
    a.node.config === b.node.config &&
    a.node.conditionGroups === b.node.conditionGroups &&
    a.node.defaultNextNodeId === b.node.defaultNextNodeId
  );
});

/** Where a line from (fromX, fromY) meets a circle of radius r around the target's centre, and
 * which side it comes in from (for the curve's direction). */
function getCirclePoint(fromX: number, fromY: number, cx: number, cy: number, r: number) {
  const dx = fromX - cx;
  const dy = fromY - cy;
  const length = Math.hypot(dx, dy) || 1;
  const position = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? Position.Right : Position.Left) : (dy > 0 ? Position.Bottom : Position.Top);
  return { x: cx + (dx / length) * r, y: cy + (dy / length) * r, position };
}

const LabeledEdgeImpl: React.FC<EdgeProps> = ({ id, source, target, sourceX: handleX, sourceY: handleY, targetX, targetY, label, markerEnd, style, data, selected }) => {
  const sourceNode = useStore(useCallback((s) => s.nodeInternals.get(source), [source]));
  const targetNode = useStore(useCallback((s) => s.nodeInternals.get(target), [target]));
  const targetWidth = targetNode?.width || NODE_WIDTH;
  // Both ends float on the circles (not around the names), each on the side facing the other
  // step — so a flow can run down, across, up or back however the steps are placed.
  const targetCenterX = (targetNode?.positionAbsolute?.x ?? targetX - targetWidth / 2) + targetWidth / 2;
  const targetCenterY = (targetNode?.positionAbsolute?.y ?? targetY) + CIRCLE / 2;
  const sourceCenterX = sourceNode?.positionAbsolute ? sourceNode.positionAbsolute.x + (sourceNode.width || NODE_WIDTH) / 2 : handleX;
  const sourceCenterY = sourceNode?.positionAbsolute ? sourceNode.positionAbsolute.y + CIRCLE / 2 : handleY;
  // Several connections between the same two steps (e.g. two buttons) sit side by side.
  const pairIndex: number = data?.pairIndex ?? 0;
  const pairCount: number = data?.pairCount ?? 1;
  const pairShift = pairCount > 1 ? (pairIndex - (pairCount - 1) / 2) * 12 : 0;
  const start = getCirclePoint(targetCenterX, targetCenterY, sourceCenterX, sourceCenterY, CIRCLE / 2 + 2);
  const sourceX = start.x + (start.position === Position.Top || start.position === Position.Bottom ? pairShift : 0);
  const sourceY = start.y + (start.position === Position.Left || start.position === Position.Right ? pairShift : 0);
  const sourcePosition = start.position;
  const rawBorder = getCirclePoint(sourceCenterX, sourceCenterY, targetCenterX, targetCenterY, CIRCLE / 2 + 2);
  // When several edges land on the same side of the same target, getBorderPoint alone can put
  // their dots on (or very near) the same pixel, so they visually merge into one and only the
  // topmost is grabbable. incomingIndex/incomingCount (passed in via edge.data from the parent,
  // computed across all edges sharing this target) spread them out along that border instead.
  const incomingIndex: number = data?.incomingIndex ?? 0;
  const incomingCount: number = data?.incomingCount ?? 1;
  const spread = incomingCount > 1 ? (incomingIndex - (incomingCount - 1) / 2) * 10 : 0;
  const editing = !!data?.editing;
  const border =
    rawBorder.position === Position.Top || rawBorder.position === Position.Bottom
      ? { ...rawBorder, x: rawBorder.x + spread }
      : { ...rawBorder, y: rawBorder.y + spread };
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX: border.x,
    targetY: border.y,
    targetPosition: border.position,
  });
  const { screenToFlowPosition, getNodes } = useReactFlow();
  const [dragPoint, setDragPoint] = useState<{ x: number; y: number } | null>(null);

  // React Flow's own reconnect-drag hit-target (EdgeAnchor) sits at the raw targetX/targetY
  // it computes from the node's fixed, invisible Position.Top handle — not at border.x/border.y,
  // which is where this "floating edge" technique actually draws the line. That mismatch made
  // the visible arrow end ungrabbable in practice. So this renders its own visible handle at
  // border.x/border.y and drives the retarget drag manually: a preview line follows the cursor
  // (via dragPoint) so the user can see what they're aiming at, instead of dragging blind.
  const handlePointerDown = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setDragPoint(screenToFlowPosition({ x: event.clientX, y: event.clientY }));

      const onMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        setDragPoint(screenToFlowPosition({ x: moveEvent.clientX, y: moveEvent.clientY }));
      };
      const onUp = (upEvent: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const flowPoint = screenToFlowPosition({ x: upEvent.clientX, y: upEvent.clientY });
        const allNodes = getNodes();
        const dropNode = allNodes.find((n) => {
          if (n.id === source) return false;
          const w = n.width || NODE_WIDTH;
          const h = n.height || CIRCLE + 30;
          const nx = n.positionAbsolute?.x ?? n.position.x;
          const ny = n.positionAbsolute?.y ?? n.position.y;
          return flowPoint.x >= nx && flowPoint.x <= nx + w && flowPoint.y >= ny && flowPoint.y <= ny + h;
        });
        setDragPoint(null);
        if (dropNode && data?.onRetarget && data?.sourceNodeId && data?.edgeKey) {
          data.onRetarget(data.sourceNodeId, data.edgeKey, dropNode.id);
        }
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [screenToFlowPosition, getNodes, source, data]
  );

  const dragging = !!dragPoint;
  const [previewPath] = dragging
    ? getBezierPath({ sourceX, sourceY, sourcePosition, targetX: dragPoint.x, targetY: dragPoint.y, targetPosition: border.position })
    : [null];

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} interactionWidth={18}
        style={{ ...style, ...(selected ? { strokeWidth: 4, stroke: colors.error as string } : {}), ...(dragging ? { opacity: 0.3 } : {}) }} />
      {dragging && previewPath && (
        <path d={previewPath} fill="none" stroke={colors.primary as string} strokeWidth={2} strokeDasharray="6 4" style={{ pointerEvents: 'none' }} />
      )}
      <EdgeLabelRenderer>
        {editing && (
          <input
            className="nodrag nopan"
            autoFocus
            defaultValue={data?.fullLabel || ''}
            placeholder={data?.renameHint || 'Name this path'}
            maxLength={60}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') data?.onRename?.((e.target as HTMLInputElement).value);
              if (e.key === 'Escape') data?.onCancelRename?.();
            }}
            onBlur={(e) => data?.onRename?.(e.target.value)}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              width: 160,
              padding: '3px 8px',
              borderRadius: 8,
              fontSize: 12,
              color: colors.textDark as string,
              background: colors.background.surface as string,
              border: `1.5px solid ${colors.primary as string}`,
              outline: 'none',
              pointerEvents: 'all',
              zIndex: 1001,
            }}
          />
        )}
        {!!label && !dragging && !editing && (
          <div
            className="nodrag nopan"
            title="Double-click to rename this path"
            onDoubleClick={(e) => { e.stopPropagation(); data?.onStartRename?.(); }}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: colors.background.surfaceRaised as string,
              padding: '1px 5px',
              borderRadius: 8,
              fontSize: 10,
              color: colors.textDark as string,
              border: `1px solid ${selected ? (colors.error as string) : String(colors.border)}`,
              pointerEvents: 'all',
              cursor: 'text',
            }}
          >
            {label}
          </div>
        )}
        {/* Visible, correctly-positioned drag handle for the line's target end — grabbing and
            dragging this onto a different node re-points the connection. While dragging, it
            follows the cursor (dragPoint) instead of snapping back to border.x/border.y, so the
            handle itself is the thing the user sees moving under their pointer. */}
        <div
          className="nodrag nopan"
          onMouseDown={handlePointerDown}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${dragging ? dragPoint.x : border.x}px, ${dragging ? dragPoint.y : border.y}px)`,
            width: dragging ? 14 : 10,
            height: dragging ? 14 : 10,
            borderRadius: '50%',
            background: colors.primary as string,
            border: `2px solid ${colors.white as string}`,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            pointerEvents: 'all',
            zIndex: dragging ? 1000 : undefined,
          }}
        />
      </EdgeLabelRenderer>
    </>
  );
};

// EdgeProps' source/target/label/selected are the fields that actually change this edge's
// visual output; sourceX/Y/targetX/Y are recomputed by React Flow on every viewport/node-move
// tick regardless, so this still re-renders during drags as expected — the memo only skips
// re-renders where the parent passed an equal edge for unrelated reasons (e.g. a sibling edge's
// data changed, forcing a new edges array reference).
const LabeledEdge = React.memo(LabeledEdgeImpl, (prev, next) => {
  return (
    prev.id === next.id &&
    prev.source === next.source &&
    prev.target === next.target &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    prev.label === next.label &&
    prev.selected === next.selected &&
    prev.style === next.style &&
    prev.data?.editing === next.data?.editing &&
    prev.data?.fullLabel === next.data?.fullLabel &&
    prev.data?.pairIndex === next.data?.pairIndex &&
    prev.data?.pairCount === next.data?.pairCount &&
    prev.data?.incomingIndex === next.data?.incomingIndex &&
    prev.data?.incomingCount === next.data?.incomingCount
  );
});

const nodeTypes = { flowNode: FlowNodeCard };
const edgeTypes = { labeled: LabeledEdge };

// reactflow's <Handle> renders a plain DOM element and expects a CSS-in-JS style object
// (not an RN StyleSheet entry), so this stays separate from cardStyles.
const handleCss: React.CSSProperties = { width: 8, height: 8, minWidth: 8, minHeight: 8, background: colors.primary as string, border: `1.5px solid ${colors.white as string}` };
// The single target handle's fixed Position.Top is only used for React Flow's internal
// bookkeeping — LabeledEdge computes the actual visual entry point itself (getCirclePoint),
// so this handle is invisible rather than drawing a redundant dot that wouldn't move.
const handleHiddenCss: React.CSSProperties = { opacity: 0, width: 1, height: 1 };

export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  nodes,
  entryNodeId,
  selectedNodeId,
  onSelectNode,
  onAddNode,
  onMoveNode,
  nodePositions,
  onConnectEdge,
  onDeleteEdge,
  onSelectionChange,
  onMoveNodes,
  selectedNodeIds = [],
  onDuplicateSelected,
  onDeleteSelected,
  onAddNodeFrom,
  viewCenterRef,
  selectedNodeIssues = [],
  onEditNode,
  onDuplicateNode,
  onDeleteNode,
  onClearSelection,
  runHighlight,
  onRenameEdge,
  onDeleteItems,
}) => {
  const cardStyles = useThemedStyles(getCardStyles);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const instanceRef = React.useRef<ReactFlowInstance | null>(null);
  const [linkMenuFor, setLinkMenuFor] = useState<string | null>(null);
  const [direction, setDirectionState] = useState<Direction>(readDirection);
  const setDirection = (next: Direction) => {
    setDirectionState(next);
    try { window.localStorage.setItem(DIRECTION_KEY, next); } catch { /* private mode */ }
  };
  // 'pan': dragging empty canvas moves it (Shift+drag draws a selection box); 'select': dragging draws the box.
  const [tool, setTool] = useState<'pan' | 'select'>('pan');
  const [arrangeOpen, setArrangeOpen] = useState(false);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);
  const [editingEdgeId, setEditingEdgeId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ nodeIds: string[]; edges: Array<{ sourceNodeId: string; edgeKey: string }> } | null>(null);
  // Start is drawn at a fixed anchor point (top-left of the canvas's default layout grid,
  // ahead of every other node) and is never draggable — it's the graph's one fixed reference
  // point, not a checker the business arranges like the rest. Every other node keeps its
  // normal saved/auto-grid position.
  const nodePositionById = useMemo(() => {
    const map: Record<string, { x: number; y: number }> = {};
    nodes.forEach((node, index) => {
      if (node.type === 'start') {
        map[node.id] = { x: 0, y: 0 };
        return;
      }
      map[node.id] = nodePositions[node.id] || { x: (index % 3) * 160, y: Math.floor(index / 3) * 130 };
    });
    return map;
  }, [nodes, nodePositions]);

  // The flow-coordinate centre of the visible canvas, for placing a new node where the user is looking.
  useEffect(() => {
    if (!viewCenterRef) return;
    viewCenterRef.current = () => {
      const instance = instanceRef.current;
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!instance || !rect) return { x: 0, y: 0 };
      return instance.screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    };
    return () => { viewCenterRef.current = null; };
  }, [viewCenterRef]);

  // A selected node that is off screen (just added, or picked from the preview) is panned into view.
  useEffect(() => {
    setLinkMenuFor(null);
    const instance = instanceRef.current;
    const rect = wrapperRef.current?.getBoundingClientRect();
    const position = selectedNodeId ? nodePositionById[selectedNodeId] : undefined;
    if (!instance || !rect || !position) return;
    const screen = instance.flowToScreenPosition({ x: position.x + NODE_WIDTH / 2, y: position.y + CIRCLE / 2 });
    const margin = 60;
    const visible = screen.x > rect.left + margin && screen.x < rect.right - margin && screen.y > rect.top + margin && screen.y < rect.bottom - margin;
    if (!visible) instance.setCenter(position.x + NODE_WIDTH / 2, position.y + CIRCLE / 2 + 60, { zoom: instance.getZoom(), duration: 300 });
    // Only when the selection changes (or the selected node moves into a new spot).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, selectedNodeId ? nodePositionById[selectedNodeId]?.x : 0, selectedNodeId ? nodePositionById[selectedNodeId]?.y : 0]);

  const selectedNode = selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) : undefined;
  const openEdges = selectedNode && selectedNode.type !== 'end' ? getNodeEdges(selectedNode).filter((e) => !e.nextNodeId) : [];
  /** Existing nodes worth linking to: ones nothing points at yet first, then the nearest. */
  const linkCandidates = useMemo(() => {
    if (!selectedNode) return [];
    const incoming = new Set(nodes.flatMap((n) => getNodeEdges(n).map((e) => e.nextNodeId)).filter(Boolean) as string[]);
    const from = nodePositionById[selectedNode.id] || { x: 0, y: 0 };
    const distance = (n: FlowNode) => Math.hypot((nodePositionById[n.id]?.x ?? 0) - from.x, (nodePositionById[n.id]?.y ?? 0) - from.y);
    return nodes
      .filter((n) => n.id !== selectedNode.id && n.type !== 'start')
      .sort((a, b) => Number(incoming.has(a.id)) - Number(incoming.has(b.id)) || distance(a) - distance(b))
      .slice(0, 6)
      .map((n) => ({ node: n, orphan: !incoming.has(n.id) }));
  }, [selectedNode, nodes, nodePositionById]);

  const rfNodes: RFNode[] = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.id,
        type: 'flowNode',
        position: nodePositionById[node.id],
        draggable: node.type !== 'start',
        data: {
          node,
          isEntry: node.id === entryNodeId,
          isSelected: node.id === selectedNodeId,
          onSelect: () => onSelectNode(node.id),
          run: runHighlight?.nodes[node.id],
          dimmed: !!runHighlight && !runHighlight.nodes[node.id],
          direction,
        },
      })),
    [nodes, entryNodeId, selectedNodeId, nodePositionById, onSelectNode, runHighlight, direction]
  );

  const rfEdges: RFEdge[] = useMemo(() => {
    const edges: RFEdge[] = [];
    const taken = new Set(runHighlight?.edges || []);
    for (const node of nodes) {
      const nodeEdges = getNodeEdges(node);
      for (const edge of nodeEdges) {
        if (!edge.nextNodeId) continue;
        const edgeId = `${node.id}:${edge.key}->${edge.nextNodeId}`;
        edges.push({
          id: edgeId,
          source: node.id,
          sourceHandle: edge.key,
          target: edge.nextNodeId,
          type: 'labeled',
          // A single unnamed "next" line needs no label; buttons, rules, routes and named paths keep theirs.
          label: nodeEdges.length > 1 || edge.named ? edge.label : undefined,
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed, color: colors.primary as string },
          style: !runHighlight ? { stroke: colors.primary as string, strokeWidth: 2 }
            : taken.has(`${node.id}->${edge.nextNodeId}`) ? { stroke: colors.status.success as string, strokeWidth: 3 }
              : { stroke: colors.primary as string, strokeWidth: 1.5, opacity: 0.2 },
          data: {
            sourceNodeId: node.id,
            edgeKey: edge.key,
            onRetarget: onConnectEdge,
            editing: editingEdgeId === edgeId,
            fullLabel: nodeEdges.length > 1 || edge.named ? edge.label : '',
            renameHint: edge.key.startsWith('button:') ? 'Button text (customers see it)' : edge.key.startsWith('ai:') ? 'Category name (the AI reads it)' : 'Name this path',
            onStartRename: () => setEditingEdgeId(edgeId),
            onCancelRename: () => setEditingEdgeId(null),
            onRename: (text: string) => {
              setEditingEdgeId(null);
              const current = nodeEdges.length > 1 || edge.named ? edge.label : '';
              if (text.trim() !== current) onRenameEdge?.(node.id, edge.key, text);
            },
          },
        });
      }
    }
    // Multiple edges converging on the same target node would otherwise all draw their end
    // dot at (near) the same border point — spread them out (see incomingIndex/incomingCount
    // in LabeledEdge) so each stays individually visible and grabbable.
    const byTarget = new Map<string, RFEdge[]>();
    const byPair = new Map<string, RFEdge[]>();
    for (const e of edges) {
      byTarget.set(e.target, [...(byTarget.get(e.target) || []), e]);
      byPair.set(`${e.source}->${e.target}`, [...(byPair.get(`${e.source}->${e.target}`) || []), e]);
    }
    for (const group of byTarget.values()) {
      group.forEach((e, i) => {
        e.data = { ...e.data, incomingIndex: i, incomingCount: group.length };
      });
    }
    for (const group of byPair.values()) {
      group.forEach((e, i) => {
        e.data = { ...e.data, pairIndex: i, pairCount: group.length };
      });
    }
    return edges;
  }, [nodes, onConnectEdge, runHighlight, editingEdgeId, onRenameEdge]);

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState(rfNodes);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState(rfEdges);

  // React Flow owns the selection: re-syncing nodes/edges from the flow keeps whatever is selected
  // (driving `selected` from props made React Flow and the editor overwrite each other in a loop).
  useEffect(() => {
    setFlowNodes((prev) => {
      const selected = new Set(prev.filter((n) => n.selected).map((n) => n.id));
      return rfNodes.map((n) => (selected.has(n.id) ? { ...n, selected: true } : n));
    });
  }, [rfNodes, setFlowNodes]);
  useEffect(() => {
    setFlowEdges((prev) => {
      const selected = new Set(prev.filter((e) => e.selected).map((e) => e.id));
      return rfEdges.map((e) => (selected.has(e.id) ? { ...e, selected: true } : e));
    });
  }, [rfEdges, setFlowEdges]);

  /** Selects exactly these nodes (Cmd/Ctrl+A, Esc, after delete); lines are unselected. */
  const selectNodes = useCallback((ids: string[]) => {
    const wanted = new Set(ids);
    setFlowNodes((prev) => prev.map((n) => (!!n.selected === wanted.has(n.id) ? n : { ...n, selected: wanted.has(n.id) })));
    setFlowEdges((prev) => prev.map((e) => (e.selected ? { ...e, selected: false } : e)));
  }, [setFlowNodes, setFlowEdges]);

  const handleNodeDragStop = useCallback(
    (_: any, node: RFNode) => {
      onMoveNode(node.id, node.position);
    },
    [onMoveNode]
  );

  // When multiple nodes are selected and dragged together, React Flow still fires
  // onNodeDragStop once per node in the group (confirmed against @reactflow/core's source —
  // there's no single "group drag stopped with all positions" event otherwise). Without this,
  // FlowForm would see N separate handleNodeDragStop calls and push N separate undo entries
  // for what the user experiences as one drag gesture. onSelectionDragStop instead fires once
  // for the whole group with every dragged node's final position, so route that through
  // onMoveNodes (plural) so the parent can commit it as a single history entry — but only when
  // more than one node actually moved; a single-node "selection" drag falls back to the normal
  // per-node path above (harmless either way, but keeps the single-node case unaffected).
  const handleSelectionDragStop = useCallback(
    (_: any, nodes: RFNode[]) => {
      if (nodes.length > 1) {
        onMoveNodes?.(nodes.map((n) => ({ nodeId: n.id, position: n.position })));
      }
    },
    [onMoveNodes]
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || !connection.sourceHandle) return;
      onConnectEdge(connection.source, connection.sourceHandle, connection.target);
    },
    [onConnectEdge]
  );

  const handleEdgesDelete = useCallback(
    (edgesToDelete: RFEdge[]) => {
      for (const edge of edgesToDelete) {
        const sourceNodeId = edge.data?.sourceNodeId || edge.source;
        const edgeKey = edge.data?.edgeKey || edge.sourceHandle;
        if (sourceNodeId && edgeKey) onDeleteEdge(sourceNodeId, edgeKey);
      }
    },
    [onDeleteEdge]
  );

  // Native multi-select (shift-click / rubber-band drag), tracked separately from the existing
  // single-select (selectedNodeId, above) which still drives the inspector-panel-open behavior
  // unchanged. Drives the bulk-action toolbar below via the parent's selectedNodeIds prop.
  const handleSelectionChange = useCallback(
    ({ nodes: selected, edges: selectedEdges }: OnSelectionChangeParams) => {
      const same = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
      const ids = selected.map((n) => n.id);
      if (!same(ids, selectedNodeIds)) onSelectionChange?.(ids);
      const edgeIds = selectedEdges.map((e) => e.id);
      setSelectedEdgeIds((prev) => (same(prev, edgeIds) ? prev : edgeIds));
    },
    [onSelectionChange, selectedNodeIds]
  );

  /** Asks before deleting: the selected steps (never Start) and lines. */
  const requestDelete = useCallback((nodeIds: string[], edgeIds: string[]) => {
    const steps = nodeIds.filter((id) => nodes.find((n) => n.id === id && n.type !== 'start'));
    const lines = flowEdges.filter((e) => edgeIds.includes(e.id) && !steps.includes(e.source))
      .map((e) => ({ sourceNodeId: e.data?.sourceNodeId || e.source, edgeKey: e.data?.edgeKey || e.sourceHandle || 'default' }));
    if (steps.length === 0 && lines.length === 0) return;
    setPendingDelete({ nodeIds: steps, edges: lines });
  }, [nodes, flowEdges]);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    if (onDeleteItems) onDeleteItems(pendingDelete.nodeIds, pendingDelete.edges);
    else {
      pendingDelete.edges.forEach((e) => onDeleteEdge(e.sourceNodeId, e.edgeKey));
      pendingDelete.nodeIds.forEach((id) => onDeleteNode?.(id));
    }
    setPendingDelete(null);
    selectNodes([]);
  }, [pendingDelete, onDeleteItems, onDeleteEdge, onDeleteNode, selectNodes]);

  // Delete / Backspace (asks first), Cmd/Ctrl+A select all, Escape clear — never while typing.
  useEffect(() => {
    const typing = () => {
      const el = document.activeElement as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (pendingDelete) {
        if (e.key === 'Enter') { e.preventDefault(); confirmDelete(); }
        if (e.key === 'Escape') { e.preventDefault(); setPendingDelete(null); }
        return;
      }
      if (typing() || editingEdgeId) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const ids = selectedNodeIds.length ? selectedNodeIds : selectedNodeId ? [selectedNodeId] : [];
        if (ids.length || selectedEdgeIds.length) {
          e.preventDefault();
          requestDelete(ids, selectedEdgeIds);
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectNodes(nodes.map((n) => n.id));
      } else if (e.key === 'Escape') {
        selectNodes([]);
        onClearSelection?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pendingDelete, confirmDelete, editingEdgeId, selectedNodeIds, selectedNodeId, selectedEdgeIds, requestDelete, selectNodes, onClearSelection, nodes]);

  /** Lays the flow out in levels from Start (down or across), in one undo step. */
  const arrange = useCallback((layout: Direction) => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const level = new Map<string, number>();
    const order: string[] = [];
    const start = nodes.find((n) => n.type === 'start') || nodes[0];
    if (!start) return;
    const queue = [start.id];
    level.set(start.id, 0);
    while (queue.length) {
      const id = queue.shift()!;
      order.push(id);
      for (const edge of getNodeEdges(byId.get(id)!)) {
        if (edge.nextNodeId && byId.has(edge.nextNodeId) && !level.has(edge.nextNodeId)) {
          level.set(edge.nextNodeId, level.get(id)! + 1);
          queue.push(edge.nextNodeId);
        }
      }
    }
    const lastLevel = Math.max(0, ...level.values());
    nodes.forEach((n) => { if (!level.has(n.id)) { level.set(n.id, lastLevel + 1); order.push(n.id); } });
    const rows = new Map<number, string[]>();
    for (const id of order) rows.set(level.get(id)!, [...(rows.get(level.get(id)!) || []), id]);
    const moves: { nodeId: string; position: { x: number; y: number } }[] = [];
    for (const [depth, ids] of rows) {
      ids.forEach((id, i) => {
        if (byId.get(id)?.type === 'start') return; // Start stays the fixed anchor at 0,0
        const across = (i - (ids.length - 1) / 2);
        const position = layout === 'vertical' ? { x: Math.round(across * 160), y: depth * 140 } : { x: depth * 200, y: Math.round(across * 120) };
        moves.push({ nodeId: id, position });
      });
    }
    onMoveNodes?.(moves);
    setDirection(layout);
    setArrangeOpen(false);
    setTimeout(() => instanceRef.current?.fitView({ padding: 0.2, duration: 300 }), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, onMoveNodes]);

  const [showMiniMap, setShowMiniMap] = useState(false);

  return (
    <View style={cardStyles.canvasContainer}>
      <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
        <ReactFlow
          onInit={(instance) => { instanceRef.current = instance; }}
          onPaneClick={onClearSelection}
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={handleNodeDragStop}
          onSelectionDragStop={handleSelectionDragStop}
          onConnect={handleConnect}
          onEdgesDelete={handleEdgesDelete}
          onSelectionChange={handleSelectionChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          nodesConnectable
          // Deleting goes through requestDelete (with a confirmation), not React Flow's own key handling.
          deleteKeyCode={null}
          selectNodesOnDrag
          multiSelectionKeyCode={['Meta', 'Control', 'Shift']}
          selectionKeyCode="Shift"
          selectionOnDrag={tool === 'select'}
          panOnDrag={tool === 'select' ? [1, 2] : true}
          selectionMode={SelectionMode.Partial}
          onEdgeDoubleClick={(_, edge) => setEditingEdgeId(edge.id)}
          onlyRenderVisibleElements
        >
          {selectedNode && selectedNodeIds.length <= 1 && (
            <NodeToolbar nodeId={selectedNode.id} isVisible position={Position.Right} offset={8}>
              <View style={cardStyles.suggest}>
                <View style={cardStyles.suggestHeader}>
                  <Text style={cardStyles.suggestTitle} numberOfLines={1}>{NODE_TYPE_META.find((m) => m.type === selectedNode.type)?.label}</Text>
                  <TouchableOpacity accessibilityLabel="Edit settings" onPress={() => onEditNode?.(selectedNode.id)} style={cardStyles.iconButton}>
                    <Ionicons name="settings-outline" size={15} color={colors.text.secondary as string} />
                  </TouchableOpacity>
                  {selectedNode.type !== 'start' && (
                    <>
                      <TouchableOpacity accessibilityLabel="Duplicate" onPress={() => onDuplicateNode?.(selectedNode.id)} style={cardStyles.iconButton}>
                        <Ionicons name="copy-outline" size={15} color={colors.text.secondary as string} />
                      </TouchableOpacity>
                      <TouchableOpacity accessibilityLabel="Delete" onPress={() => requestDelete([selectedNode.id], [])} style={cardStyles.iconButton}>
                        <Ionicons name="trash-outline" size={15} color={colors.error as string} />
                      </TouchableOpacity>
                    </>
                  )}
                </View>
                {selectedNodeIssues.length > 0 && (
                  <TouchableOpacity onPress={() => onEditNode?.(selectedNode.id)} style={cardStyles.issues} activeOpacity={0.8}>
                    {selectedNodeIssues.slice(0, 3).map((issue, i) => (
                      <View key={i} style={cardStyles.issueRow}>
                        <Ionicons name="alert-circle" size={12} color={colors.warning as string} />
                        <Text style={cardStyles.issueText} numberOfLines={2}>{issue}</Text>
                      </View>
                    ))}
                    <Text style={cardStyles.issueAction}>Set up in the side panel →</Text>
                  </TouchableOpacity>
                )}
                {openEdges.slice(0, 4).map((edge) => (
                  <View key={edge.key} style={cardStyles.edgeBlock}>
                    <Text style={cardStyles.edgeTitle} numberOfLines={1}>{openEdges.length === 1 && getNodeEdges(selectedNode).length === 1 ? 'Then' : edge.label}</Text>
                    <View style={cardStyles.chipRow}>
                      {suggestNextNodeTypes(selectedNode.type).map((type) => {
                        const meta = NODE_TYPE_META.find((m) => m.type === type);
                        if (!meta) return null;
                        return (
                          <TouchableOpacity key={type} accessibilityLabel={`Add ${meta.label}`} onPress={() => onAddNodeFrom?.(selectedNode.id, edge.key, type)} style={cardStyles.chip}>
                            <AppIcon name={meta.icon} size={12} color={meta.color} />
                            <Text style={cardStyles.chipText}>{meta.label}</Text>
                          </TouchableOpacity>
                        );
                      })}
                      <TouchableOpacity accessibilityLabel="More step types" onPress={() => onAddNodeFrom?.(selectedNode.id, edge.key)} style={cardStyles.chip}>
                        <Ionicons name="add" size={12} color={colors.primary as string} />
                        <Text style={cardStyles.chipText}>More</Text>
                      </TouchableOpacity>
                      {linkCandidates.length > 0 && (
                        <TouchableOpacity accessibilityLabel="Link to an existing step" onPress={() => setLinkMenuFor((v) => (v === edge.key ? null : edge.key))} style={[cardStyles.chip, linkMenuFor === edge.key && cardStyles.chipOn]}>
                          <Ionicons name="link-outline" size={12} color={colors.primary as string} />
                          <Text style={cardStyles.chipText}>Link to…</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    {linkMenuFor === edge.key && (
                      <View style={cardStyles.linkMenu}>
                        {linkCandidates.map(({ node: candidate, orphan }) => {
                          const meta = NODE_TYPE_META.find((m) => m.type === candidate.type);
                          return (
                            <TouchableOpacity key={candidate.id} onPress={() => { setLinkMenuFor(null); onConnectEdge(selectedNode.id, edge.key, candidate.id); }} style={cardStyles.linkRow}>
                              <AppIcon name={(meta?.icon || 'ellipse-outline')} size={13} color={meta?.color || (colors.primary as string)} />
                              <Text style={cardStyles.linkText} numberOfLines={1}>{candidate.name || meta?.label}</Text>
                              {orphan && <Text style={cardStyles.linkHint}>not linked yet</Text>}
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    )}
                  </View>
                ))}
                {openEdges.length > 4 && <Text style={cardStyles.issueText}>+{openEdges.length - 4} more open connections — drag from the dots</Text>}
                {openEdges.length === 0 && selectedNodeIssues.length === 0 && selectedNode.type !== 'end' && (
                  <View style={cardStyles.issueRow}>
                    <Ionicons name="checkmark-circle" size={12} color={colors.success as string} />
                    <Text style={cardStyles.issueText}>Set up and connected</Text>
                  </View>
                )}
              </View>
            </NodeToolbar>
          )}
          <Background color={String(colors.border)} gap={20} />
          <Controls showInteractive={false} />
          {showMiniMap && <MiniMap pannable zoomable style={{ background: colors.background.surfaceRaised as string }} />}
        </ReactFlow>
      </div>
      <View style={cardStyles.toolbar}>
        <TouchableOpacity accessibilityLabel="Pan tool" style={[cardStyles.toolButton, tool === 'pan' && cardStyles.toolButtonOn]} onPress={() => setTool('pan')}>
          <Ionicons name="hand-left-outline" size={15} color={tool === 'pan' ? (colors.primary as string) : (colors.text.secondary as string)} />
        </TouchableOpacity>
        <TouchableOpacity accessibilityLabel="Select tool: drag to select several steps" style={[cardStyles.toolButton, tool === 'select' && cardStyles.toolButtonOn]} onPress={() => setTool('select')}>
          <Ionicons name="scan-outline" size={15} color={tool === 'select' ? (colors.primary as string) : (colors.text.secondary as string)} />
        </TouchableOpacity>
        <View style={cardStyles.toolDivider} />
        <TouchableOpacity
          accessibilityLabel={direction === 'vertical' ? 'Connection dots below the steps (top to bottom). Switch to left to right' : 'Connection dots right of the steps (left to right). Switch to top to bottom'}
          style={cardStyles.toolButton}
          onPress={() => setDirection(direction === 'vertical' ? 'horizontal' : 'vertical')}
        >
          <Ionicons name={direction === 'vertical' ? 'arrow-down' : 'arrow-forward'} size={15} color={colors.text.secondary as string} />
        </TouchableOpacity>
        <TouchableOpacity accessibilityLabel="Arrange the flow" style={[cardStyles.toolButton, arrangeOpen && cardStyles.toolButtonOn]} onPress={() => setArrangeOpen((v) => !v)}>
          <Ionicons name="git-network-outline" size={15} color={colors.text.secondary as string} />
        </TouchableOpacity>
        {arrangeOpen && (
          <View style={cardStyles.arrangeMenu}>
            <TouchableOpacity style={cardStyles.arrangeItem} onPress={() => arrange('vertical')}>
              <Ionicons name="arrow-down" size={14} color={colors.text.primary as string} />
              <Text style={cardStyles.arrangeText}>Arrange top to bottom</Text>
            </TouchableOpacity>
            <TouchableOpacity style={cardStyles.arrangeItem} onPress={() => arrange('horizontal')}>
              <Ionicons name="arrow-forward" size={14} color={colors.text.primary as string} />
              <Text style={cardStyles.arrangeText}>Arrange left to right</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      {pendingDelete && (
        <View style={cardStyles.confirmWrap}>
          <View style={cardStyles.confirmCard}>
            <Text style={cardStyles.confirmTitle}>
              Delete {[pendingDelete.nodeIds.length ? `${pendingDelete.nodeIds.length} step${pendingDelete.nodeIds.length > 1 ? 's' : ''}` : '', pendingDelete.edges.length ? `${pendingDelete.edges.length} line${pendingDelete.edges.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' and ')}?
            </Text>
            <Text style={cardStyles.confirmText}>
              {pendingDelete.nodeIds.length ? `${pendingDelete.nodeIds.map((id) => nodes.find((n) => n.id === id)?.name || 'Untitled').slice(0, 4).join(', ')}${pendingDelete.nodeIds.length > 4 ? '…' : ''}. Lines into them are removed too. ` : ''}You can undo with Ctrl/Cmd+Z.
            </Text>
            <View style={cardStyles.confirmActions}>
              <TouchableOpacity style={cardStyles.confirmCancel} onPress={() => setPendingDelete(null)}>
                <Text style={cardStyles.confirmCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={cardStyles.confirmDelete} onPress={confirmDelete}>
                <Text style={cardStyles.confirmDeleteText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
      <TouchableOpacity style={cardStyles.addButton} onPress={onAddNode} activeOpacity={0.85}>
        <Ionicons name="add" size={22} color={colors.white} />
      </TouchableOpacity>
      <TouchableOpacity style={cardStyles.miniMapToggle} onPress={() => setShowMiniMap((v) => !v)} activeOpacity={0.85}>
        <Ionicons name={showMiniMap ? 'map' : 'map-outline'} size={18} color={showMiniMap ? colors.primary : colors.textLight} />
      </TouchableOpacity>
      {selectedNodeIds.length > 1 && (
        <View style={cardStyles.bulkToolbarWrap} pointerEvents="box-none">
          <View style={cardStyles.bulkToolbar}>
            <Text style={cardStyles.bulkToolbarCount}>{selectedNodeIds.length} selected</Text>
            <TouchableOpacity style={cardStyles.bulkToolbarButton} onPress={onDuplicateSelected} activeOpacity={0.85}>
              <Ionicons name="copy-outline" size={16} color={colors.text.primary as string} />
              <Text style={cardStyles.bulkToolbarButtonText}>Duplicate</Text>
            </TouchableOpacity>
            <TouchableOpacity style={cardStyles.bulkToolbarButton} onPress={() => requestDelete(selectedNodeIds, selectedEdgeIds)} activeOpacity={0.85}>
              <Ionicons name="trash-outline" size={16} color={colors.error as string} />
              <Text style={[cardStyles.bulkToolbarButtonText, { color: colors.error as string }]}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
      <View style={cardStyles.hint}>
        <Text style={cardStyles.hintText}>Select a step for suggestions · drag a dot to connect · drag a line's end to re-point it · double-click a line to name it · Delete removes the selection · Shift/Cmd-click or the select tool picks several</Text>
      </View>
    </View>
  );
};

const getCardStyles = () => StyleSheet.create({
  canvasContainer: {
    width: '100%',
    flex: 1,
    minHeight: 400,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    position: 'relative',
  },
  node: {
    width: NODE_WIDTH,
    alignItems: 'center',
  },
  circle: {
    width: CIRCLE,
    height: CIRCLE,
    borderRadius: CIRCLE / 2,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...designTokens.shadows.sm,
  },
  // Start is the fixed anchor, not a draggable checker: dashed ring.
  startCircle: {
    borderStyle: 'dashed',
  },
  runBadge: {
    position: 'absolute',
    top: -6,
    left: -8,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.background.surface,
  },
  runBadgeText: {
    fontSize: 9,
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
  },
  entryBadge: {
    position: 'absolute',
    top: -3,
    right: -3,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.background.surface,
  },
  title: {
    marginTop: 6,
    fontSize: 11,
    lineHeight: 14,
    textAlign: 'center',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  suggest: {
    width: 248,
    padding: spacing.sm,
    gap: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
    ...designTokens.shadows.md,
  },
  suggestHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  suggestTitle: {
    flex: 1,
    fontSize: 12,
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  iconButton: {
    padding: 4,
    borderRadius: designTokens.borderRadius.sm,
  },
  issues: {
    gap: 3,
    padding: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: `${String(colors.warning)}14`,
  },
  issueRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  issueText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 14,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  issueAction: {
    fontSize: 11,
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  edgeBlock: {
    gap: 4,
    paddingTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  edgeTitle: {
    fontSize: 11,
    color: colors.textLight,
    fontFamily: typography.fontFamily.semibold,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  chipOn: {
    borderColor: colors.primary,
  },
  chipText: {
    fontSize: 11,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  linkMenu: {
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  linkText: {
    flex: 1,
    fontSize: 12,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.primary,
  },
  linkHint: {
    fontSize: 10,
    color: colors.warning,
    fontFamily: typography.fontFamily.medium,
  },
  toolbar: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
    ...designTokens.shadows.sm,
  },
  toolButton: { width: 28, height: 28, borderRadius: designTokens.borderRadius.sm, alignItems: 'center', justifyContent: 'center' },
  toolButtonOn: { backgroundColor: `${String(colors.primary)}1A` },
  toolDivider: { width: 1, height: 18, backgroundColor: colors.border, marginHorizontal: 2 },
  arrangeMenu: {
    position: 'absolute',
    top: 36,
    right: 0,
    minWidth: 190,
    padding: 4,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
    ...designTokens.shadows.md,
  },
  arrangeItem: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, paddingVertical: 7, borderRadius: designTokens.borderRadius.sm },
  arrangeText: { fontSize: 12, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  confirmWrap: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.25)' },
  confirmCard: {
    width: 320,
    maxWidth: '90%' as any,
    padding: spacing.md,
    gap: spacing.sm,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: colors.background.surface,
    ...designTokens.shadows.md,
  },
  confirmTitle: { fontSize: 15, color: colors.textDark, fontFamily: typography.fontFamily.bold },
  confirmText: { fontSize: 12, lineHeight: 17, color: colors.text.secondary, fontFamily: typography.fontFamily.primary },
  confirmActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.xs },
  confirmCancel: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border },
  confirmCancelText: { fontSize: 13, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  confirmDelete: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.error },
  confirmDeleteText: { fontSize: 13, color: colors.white, fontFamily: typography.fontFamily.semibold },
  addButton: {
    position: 'absolute',
    bottom: spacing.md,
    right: spacing.md,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.md,
  },
  // React Flow's own <Controls> renders a vertical stack of ~34px buttons anchored at
  // bottom-left (default ~10px margin) — this sits just above that stack so the two never
  // overlap regardless of exact Controls sizing.
  miniMapToggle: {
    position: 'absolute',
    bottom: 160,
    left: spacing.sm,
    width: 30,
    height: 30,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  // Full-width, non-visual wrapper so the actual toolbar can be centered via flexbox instead
  // of a magic-number translateX — pointerEvents="box-none" lets clicks pass through the empty
  // left/right space to the canvas underneath, only the toolbar itself is interactive.
  bulkToolbarWrap: {
    position: 'absolute',
    top: spacing.md,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  bulkToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    ...designTokens.shadows.md,
  },
  bulkToolbarCount: {
    fontSize: 12,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
    marginRight: spacing.xs,
  },
  bulkToolbarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: designTokens.borderRadius.sm,
  },
  bulkToolbarButtonText: {
    fontSize: 12,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.medium,
  },
  hint: {
    position: 'absolute',
    bottom: spacing.md,
    left: spacing.md,
    right: 60,
    backgroundColor: `${String(colors.background.surfaceRaised)}E6`,
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
  },
  hintText: {
    fontSize: 10,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});
